import { Row, Col, Button } from 'reactstrap';
import { toUSD } from '~/shared/utils';
import { MdOutlineReceiptLong } from 'react-icons/md';
import { MdOutlineCancel } from 'react-icons/md';
import { FaFlag } from 'react-icons/fa6';
import { FaCheckCircle } from 'react-icons/fa';
import { useContext, useState, useEffect } from 'react';
import { FundRequestContext } from '~/context/fundrequests';
import { renderIcon } from '~/shared/components/icons';
import { PartialDisbursementModal } from './PartialDisbursementModal';
import { FileViewerModal } from '../../../SubmissionConfirmation/FileViewerModal';

const ShowExpenses = ({
    defaultExpensesFieldArray,
    travelExpensesFieldArray,
    foodExpensesFieldArray,
    fundraisingExpensesFieldArray,
    medicationExpensesFieldArray,
    insuranceExpensesFieldArray,
    relocationLodgingExpensesFieldArray,
    otherExpensesFieldArray,
    physicalTherapyExpensesFieldArray,
    careGivingExpensesFieldArray,
}: any) => {
    const [{ categories, fundRequest }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [showPartialModal, setShowPartialModal] = useState(false);
    const [showPartialAcceptedMessage, setShowPartialAcceptedMessage] = useState(false);
    const [hasShownModalOnLoad, setHasShownModalOnLoad] = useState(false);
    const [fileViewerModal, setFileViewerModal] = useState<{
        isOpen: boolean;
        fileUrl: string;
        filename: string;
        files?: Array<{ url: string; filename: string }>;
        currentIndex?: number;
    }>({
        isOpen: false,
        fileUrl: '',
        filename: '',
        files: [],
        currentIndex: 0
    });

    // Auto-show modal on page load if partial status is suggested
    useEffect(() => {
        if (fundRequest?.partial_status === 'suggested' && !hasShownModalOnLoad) {
            setShowPartialModal(true);
            setHasShownModalOnLoad(true);
        }
    }, [fundRequest?.partial_status, hasShownModalOnLoad]);

    // Show success message when partial status becomes accepted
    useEffect(() => {
        if (fundRequest?.partial_status === 'accepted') {
            setShowPartialAcceptedMessage(true);
        }
    }, [fundRequest?.partial_status]);

    const defaultItems = defaultExpensesFieldArray.fields;
    const travelItems = travelExpensesFieldArray.fields;
    const foodItems = foodExpensesFieldArray.fields;
    const fundraisingItems = fundraisingExpensesFieldArray.fields;
    const medicationItems = medicationExpensesFieldArray.fields;
    const insuranceItems = insuranceExpensesFieldArray.fields;
    const relocationLodgingItems = relocationLodgingExpensesFieldArray.fields;
    const otherItems = otherExpensesFieldArray.fields;
    const physicalTherapyItems = physicalTherapyExpensesFieldArray.fields;
    const careGivingItems = careGivingExpensesFieldArray.fields;

    const defaultTotal = defaultItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const travelTotal = travelItems.reduce((acc: number, item: any) => {
        const amount = Number(item.amount);
        return amount ? acc + amount : acc;
    }, 0);

    const foodTotal = foodItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const fundraisingTotal = fundraisingItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const medicationTotal = medicationItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const insuranceTotal = insuranceItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const relocationLodgingTotal = relocationLodgingItems.reduce(
        (acc: number, item: any) => {
            return acc + Number(item.amount);
        },
        0
    );

    const otherTotal = otherItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const physicalTherapyTotal = physicalTherapyItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const careGivingTotal = careGivingItems.reduce((acc: number, item: any) => {
        return acc + Number(item.amount);
    }, 0);

    const grandTotal =
        defaultTotal +
        travelTotal +
        foodTotal +
        fundraisingTotal +
        medicationTotal +
        insuranceTotal +
        relocationLodgingTotal +
        otherTotal +
        physicalTherapyTotal +
        careGivingTotal;

    const getFormattedLabels = {
        fundraising: 'Fundraising',
        medical: 'Medical',
        travel_mileage: 'Travel/Mileage',
        food_incidentals: 'Food/Incidentals',
        medications: 'Medications',
        insurance: 'Insurance',
        caregiving: 'Caregiving',
        physical_therapy: 'Physical Therapy',
        relocation_lodging: 'Relocation/Lodging',
        other: 'Other',
    };

    const handleExpenseItemClick = (
        expenseItem,
        expenseCategory,
        expenseType,
        expenseIndex
    ) => {
        fundRequestDispatch({
            type: 'set_expense_info',
            expenseItem,
            expenseCategory,
            expenseType,
            expenseIndex,
        });

        // Set edit mode to true to display the form
        fundRequestDispatch({
            type: 'set_edit_mode',
            payload: true,
        });
    };

    const handleReceiptClick = (uploads: any[]) => {
        // Map all uploads to the format expected by FileViewerModal
        const files = uploads.map(upload => ({
            url: upload.image_url,
            filename: upload.filename || 'Receipt'
        }));
        
        setFileViewerModal({
            isOpen: true,
            fileUrl: files[0]?.url || '',
            filename: files[0]?.filename || 'Receipt',
            files: files,
            currentIndex: 0
        });
    };

    const closeFileViewer = () => {
        setFileViewerModal({
            isOpen: false,
            fileUrl: '',
            filename: '',
            files: [],
            currentIndex: 0
        });
    };

    const renderExpenseSection = (fields, categoryName, remove) => {
        // Check that at least one field item has an integer id
        const hasValidId = fields.some((item) => typeof item.id === 'number');
        if (!hasValidId) {
            return null; // Skip rendering if item has not been saved
        }

        const category = categories.find((c) => c.value === categoryName);
        let categoryTotal: number = 0;

        if (categoryName === 'fundraising') {
            categoryTotal = fundraisingTotal;
        } else if (categoryName === 'medical') {
            categoryTotal = defaultTotal;
        } else if (categoryName === 'travel_mileage') {
            categoryTotal = travelTotal;
        } else if (categoryName === 'food_incidentals') {
            categoryTotal = foodTotal;
        } else if (categoryName === 'medications') {
            categoryTotal = medicationTotal;
        } else if (categoryName === 'insurance') {
            categoryTotal = insuranceTotal;
        } else if (categoryName === 'relocation_lodging')
            categoryTotal = relocationLodgingTotal;
        else if (categoryName === 'other') {
            categoryTotal = otherTotal;
        } else if (categoryName === 'caregiving') {
            categoryTotal = careGivingTotal;
        } else if (categoryName === 'physical_therapy') {
            categoryTotal = physicalTherapyTotal;
        }

        return (
            <div style={{ marginBottom: '20px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #E5E7EB',
                        paddingBottom: '8px',
                        marginBottom: '12px',
                    }}
                >
                    <span
                        style={{
                            fontSize: '20px',
                            fontWeight: '400',
                            color: '#333',
                        }}
                    >
                        {getFormattedLabels[categoryName]}
                    </span>
                    <span
                        style={{
                            fontSize: '17px',
                            fontWeight: '400',
                            color: '#333',
                        }}
                    >
                        {toUSD(categoryTotal)}
                    </span>
                </div>
                {fields.map((item, index) => {
                    if (typeof item.id !== 'number') {
                        return null;
                    }

                    const expenseDate =
                        item?.expense_date ||
                        item?.date_of_travel ||
                        item?.start_date;
                    const description =
                        item?.description ||
                        item?.recipient_name ||
                        item?.destination;

                    let itemTotal,
                        itemType = null;
                    if (
                        [
                            'medical',
                            'medications',
                            'insurance',
                            'relocation_lodging',
                            'caregiving',
                            'physical_therapy',
                            'other',
                            'fundraising',
                        ].includes(categoryName)
                    ) {
                        itemTotal = item.amount;
                        itemType = 'default';
                    } else if (categoryName === 'travel_mileage') {
                        itemTotal = item.amount;
                        itemType = 'travel';
                    } else if (categoryName === 'food_incidentals') {
                        itemTotal = item.amount;
                        itemType = 'food';
                    }
                    return (
                        <Row
                            key={item.id}
                            style={{
                                marginBottom: '12px',
                                paddingBottom: '12px',
                                borderBottom: '1px solid #E5E7EB',
                            }}
                        >
                            <Col sm={4} lg={6} style={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    color='link'
                                    style={{
                                        padding: 0,
                                        marginRight: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    onClick={() => remove(index)}
                                >
                                    <MdOutlineCancel size={21} color='red' />
                                </Button>
                                <span
                                    style={{
                                        color: '#00BCD4',
                                        fontWeight: '400',
                                        fontSize: '17px',
                                        cursor: 'pointer',
                                        marginRight: '8px',
                                    }}
                                    onClick={() =>
                                        handleExpenseItemClick(
                                            item,
                                            category,
                                            itemType,
                                            index
                                        )
                                    }
                                >
                                    {description || "No description"}
                                </span>

                                <Button
                                    onClick={() =>
                                        handleExpenseItemClick(
                                            item,
                                            category,
                                            itemType,
                                            index
                                        )
                                    }
                                    color='white'
                                    className='p-1 m-0 text-primary'
                                    style={{
                                        fontSize: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {renderIcon('edit_default')}
                                </Button>
                            </Col>

                            <Col xs={6} sm={4} lg={3} className='text-sm-end'>
                                <span
                                    style={{
                                        color: '#666',
                                        fontSize: '17px',
                                        fontWeight: '400',
                                        marginLeft: 'auto',
                                    }}
                                >
                                    {expenseDate || "No date"}
                                </span>
                            </Col>

                            <Col xs={2} sm={1}>
                                {item.uploads &&
                                    item.uploads.length > 0 &&
                                    item.uploads[0].image_url && (
                                        <Button
                                            color='link'
                                            style={{
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: '#6B7280',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => handleReceiptClick(item.uploads)}
                                            title="View receipt"
                                        >
                                            <MdOutlineReceiptLong size={23} />
                                            {item.uploads.length > 1 && (
                                                <span style={{ 
                                                    marginLeft: '4px', 
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    color: '#6B7280'
                                                }}>
                                                    ({item.uploads.length})
                                                </span>
                                            )}
                                        </Button>
                                    )}
                            </Col>

                            <Col xs={4} sm={3} lg={2} className='text-end'>
                                <span
                                    style={{
                                        color: '#333',
                                        fontSize: '17px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {toUSD(itemTotal)}
                                </span>
                            </Col>
                            <Col xs={12}>
                                {item.flagged_comment && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            backgroundColor: '#ffeaea',
                                            color: '#ff4d4d',
                                            padding: '5px 15px',
                                            borderRadius: '8px',
                                            marginTop: '10px',
                                        }}
                                    >
                                        <FaFlag
                                            style={{
                                                marginRight: '10px',
                                                color: '#ff4d4d',
                                                fontSize: '1.2rem',
                                                marginTop: '2px',
                                            }}
                                        />

                                        <span>{item.flagged_comment}</span>
                                    </div>
                                )}
                            </Col>
                        </Row>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            {
                (Boolean(fundraisingExpensesFieldArray?.fields?.length) ||
                Boolean(foodExpensesFieldArray?.fields?.length) ||
                Boolean(travelExpensesFieldArray?.fields?.length) ||
                Boolean(defaultExpensesFieldArray?.fields?.length) ||
                Boolean(defaultExpensesFieldArray?.fields?.length) ||
                Boolean(medicationExpensesFieldArray?.fields?.length) ||
                Boolean(insuranceExpensesFieldArray?.fields?.length) ||
                Boolean(relocationLodgingExpensesFieldArray?.fields?.length) ||
                Boolean(otherExpensesFieldArray?.fields?.length) ||
                Boolean(physicalTherapyExpensesFieldArray?.fields?.length) ||
                Boolean(careGivingExpensesFieldArray?.fields?.length)) && (
                    <div style={{ marginTop: 20 }}>
                        {Boolean(defaultExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                defaultExpensesFieldArray.fields,
                                'medical',
                                defaultExpensesFieldArray.remove
                            )}
                        {Boolean(travelExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                travelExpensesFieldArray.fields,
                                'travel_mileage',
                                travelExpensesFieldArray.remove
                            )}
                        {Boolean(foodExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                foodExpensesFieldArray.fields,
                                'food_incidentals',
                                foodExpensesFieldArray.remove
                            )}
                        {Boolean(fundraisingExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                fundraisingExpensesFieldArray.fields,
                                'fundraising',
                                fundraisingExpensesFieldArray.remove
                            )}
                            
                        {Boolean(medicationExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                medicationExpensesFieldArray.fields,
                                'medications',
                                medicationExpensesFieldArray.remove
                            )}
                        {Boolean(insuranceExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                insuranceExpensesFieldArray.fields,
                                'insurance',
                                insuranceExpensesFieldArray.remove
                            )}
                        {Boolean(
                            relocationLodgingExpensesFieldArray?.fields?.length
                        ) &&
                            renderExpenseSection(
                                relocationLodgingExpensesFieldArray.fields,
                                'relocation_lodging',
                                relocationLodgingExpensesFieldArray.remove
                            )}
                        {Boolean(otherExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                otherExpensesFieldArray.fields,
                                'other',
                                otherExpensesFieldArray.remove
                            )}
                        {Boolean(
                            physicalTherapyExpensesFieldArray?.fields?.length
                        ) &&
                            renderExpenseSection(
                                physicalTherapyExpensesFieldArray.fields,
                                'physical_therapy',
                                physicalTherapyExpensesFieldArray.remove
                            )}
                        {Boolean(careGivingExpensesFieldArray?.fields?.length) &&
                            renderExpenseSection(
                                careGivingExpensesFieldArray.fields,
                                'caregiving',
                                careGivingExpensesFieldArray.remove
                            )}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                borderTop: '1px solid #E5E7EB',
                                paddingTop: '12px',
                            }}
                        >
                            <span
                                style={{
                                    fontSize: '17px',
                                    fontWeight: '400',
                                    color: '#333',
                                }}
                            >
                                Total
                            </span>
                            <span
                                style={{
                                    fontSize: '17px',
                                    fontWeight: '400',
                                    color: '#333',
                                }}
                            >
                                {toUSD(grandTotal)}
                            </span>
                        </div>
                        
                        {/* Partial Disbursement Warning */}
                        {fundRequest?.partial_status === 'suggested' && (
                            <div
                                style={{
                                    backgroundColor: '#FEF2F2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginTop: '16px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                }}
                            >
                                <FaFlag
                                    size={16}
                                    style={{
                                        color: '#DC2626',
                                        marginTop: '2px',
                                        flexShrink: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        color: '#DC2626',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        lineHeight: '1.4',
                                    }}
                                >
                                    You have insufficient funds. HHL recommends a Partial Disbursement.{' '}
                                    <span
                                        style={{
                                            textDecoration: 'underline',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => setShowPartialModal(true)}
                                    >
                                        Review the message from the Finance Team.
                                    </span>
                                </span>
                            </div>
                        )}

                        {/* Partial Disbursement Accepted Success Message */}
                        {showPartialAcceptedMessage && fundRequest?.partial_status === 'accepted' && (
                            <div
                                style={{
                                    backgroundColor: '#F0FDF4',
                                    border: '1px solid #BBF7D0',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginTop: '16px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '8px',
                                }}
                            >
                                <FaCheckCircle
                                    size={16}
                                    style={{
                                        color: '#16A34A',
                                        marginTop: '2px',
                                        flexShrink: 0,
                                    }}
                                />
                                <span
                                    style={{
                                        color: '#16A34A',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        lineHeight: '1.4',
                                    }}
                                >
                                    Accepting Partial Disbursement
                                </span>
                            </div>
                        )}


                    </div>
                )}

        <PartialDisbursementModal
            isOpen={showPartialModal}
            toggle={() => setShowPartialModal(false)}
            fundRequest={fundRequest}
        />
        <FileViewerModal
            isOpen={fileViewerModal.isOpen}
            toggle={closeFileViewer}
            fileUrl={fileViewerModal.fileUrl}
            filename={fileViewerModal.filename}
            files={fileViewerModal.files}
            currentIndex={fileViewerModal.currentIndex}
        />
        </>
    );
};

export default ShowExpenses;
